import * as v from 'valibot';
import { FileType } from '~/.server/drizzle/entity/FileEntity';
import form from '~/components/valibot/form';
import { FileImageSizes } from '~common/util/cdn';
import { FILM_KINDS } from '~/lib/films';

export type DeliverableFileImage = FileImageSizes;
export type DeliverableFileVideo = { m3u8: string; mp4: string };

export interface DeliverableFile {
  id: string;
  type: FileType;
  name: string;
  pdf?: string;
  image?: DeliverableFileImage;
  video?: DeliverableFileVideo;
}

export enum DeliverableType {
  PHOTO = 'PHOTO',
  VIDEO = 'VIDEO',
  SOCIAL = 'SOCIAL',
  SLIDESHOW = 'SLIDESHOW',
  BROCHURE = 'BROCHURE',
  AERIAL = 'AERIAL',
  MATTERPORT = 'MATTERPORT',
  FLOORPLAN = 'FLOORPLAN',
  NUCLEUS = 'NUCLEUS',
  IGUIDE = 'IGUIDE',
  /**
   * The drone's 360° sphere: a DJI equirectangular panorama JPG (2:1, ~104
   * MP). Not a photo — kept out of the gallery, zips, slideshows and the
   * listing handoff's photos[], and handed on in its own field. Never chosen
   * by hand: an uploaded AERIAL/PHOTO file is reclassified once it is read
   * (worker/admin/src/.server/sphere-deliverable.ts).
   */
  SPHERE = 'SPHERE',
}

export function deliverable_is_walkthrough(type: DeliverableType): boolean {
  return type === DeliverableType.MATTERPORT || type === DeliverableType.NUCLEUS || type === DeliverableType.IGUIDE;
}

export enum DeliverableVisibility {
  BOTH = 'BOTH',
  BRANDED = 'BRANDED',
  UNBRANDED = 'UNBRANDED',
  NONE = 'NONE',
}

export const DeliverableTypeName: Record<DeliverableType, string> = {
  [DeliverableType.AERIAL]: 'Aerial',
  [DeliverableType.MATTERPORT]: 'Matterport',
  [DeliverableType.FLOORPLAN]: 'Floorplans',
  [DeliverableType.SOCIAL]: 'Social',
  [DeliverableType.BROCHURE]: 'Brochures',
  [DeliverableType.PHOTO]: 'Photos',
  [DeliverableType.VIDEO]: 'Videos',
  [DeliverableType.SLIDESHOW]: 'Slideshow',
  [DeliverableType.NUCLEUS]: '3D Walkthrough',
  [DeliverableType.IGUIDE]: '3D Walkthrough',
  [DeliverableType.SPHERE]: '360° view',
};

export const DeliverableTypes = Object.values(DeliverableType);

export interface Deliverable<T extends DeliverableType | any = any> {
  id: string;
  data: T extends DeliverableType ? Extract<DeliverableData, { type: T }> : DeliverableData;
  file?: DeliverableFile;
  created?: TDateISO;
}

export const DeliverableDataPhotoSchema = v.object({
  type: v.literal(DeliverableType.PHOTO),
  caption: v.optional(v.string()),
});

export type DeliverableDataPhoto = v.InferOutput<typeof DeliverableDataPhotoSchema>;

export const DeliverableDataSocialSchema = v.object({
  type: v.literal(DeliverableType.SOCIAL),
  caption: v.optional(v.string()),
  raw: v.optional(form.boolean),
  visibility: v.optional(v.enum(DeliverableVisibility)),
});

export type DeliverableDataSocial = v.InferOutput<typeof DeliverableDataSocialSchema>;

export const DeliverableDataVideoSchema = v.object({
  type: v.literal(DeliverableType.VIDEO),
  caption: v.optional(v.string()),
  branded: v.optional(form.boolean),
  raw: v.optional(form.boolean),
  /**
   * A FILM made after the shoot and attached to the order when it landed
   * (worker/admin/src/.server/films/): which one. It never goes back out in
   * the hand-off that made it.
   */
  film: v.optional(v.picklist(FILM_KINDS)),
});

export type DeliverableDataVideo = v.InferOutput<typeof DeliverableDataVideoSchema>;

export const DeliverableDataAerialSchema = v.object({
  type: v.literal(DeliverableType.AERIAL),
  caption: v.optional(v.string()),
  boundary: v.optional(v.pipe(v.unknown(), v.transform(Boolean)), false),
  markers: v.optional(v.pipe(v.unknown(), v.transform(Boolean)), false),
});

export type DeliverableDataAerial = v.InferOutput<typeof DeliverableDataAerialSchema>;

export const DeliverableDataBrochureSchema = v.object({
  type: v.literal(DeliverableType.BROCHURE),
  profile_id: v.optional(v.string()),
});

export type DeliverableDataBrochure = v.InferOutput<typeof DeliverableDataBrochureSchema>;

export const DeliverableDataSlideshowSchema = v.object({
  type: v.literal(DeliverableType.SLIDESHOW),
  profile_id: v.optional(v.string()),
  generating: v.boolean(),
});

export type DeliverableDataSlideshow = v.InferOutput<typeof DeliverableDataSlideshowSchema>;

export const DeliverableDataFloorplanSchema = v.object({
  type: v.literal(DeliverableType.FLOORPLAN),
  floors: v.fallback(v.array(form.number()), []),
});

export type DeliverableDataFloorplan = v.InferOutput<typeof DeliverableDataFloorplanSchema>;

export const DeliverableDataMatterportSchema = v.object({
  type: v.literal(DeliverableType.MATTERPORT),
  sort: form.optional(form.number()),
  url: v.pipe(
    v.string('A branded URL is required.'),
    v.url(),
    v.check(
      (url: string) => url.startsWith('https://my.matterport.com/show/?m='),
      'The URL must start with "https://my.matterport.com/show/?m=".',
    ),
    v.transform((url: string) => {
      const parsed = new URL(url);

      const m = parsed.searchParams.get('m');

      // Remove Matterport specific variables
      // https://support.matterport.com/s/article/URL-Parameters?language=en_US
      parsed.search = '';

      if (m) {
        parsed.searchParams.set('m', m);
      }

      return parsed.toString();
    }),
  ),
});

export type DeliverableDataMatterport = v.InferOutput<typeof DeliverableDataMatterportSchema>;

export const DeliverableDataNucleusSchema = v.object({
  type: v.literal(DeliverableType.NUCLEUS),
  sort: form.optional(form.number()),
  url: v.pipe(
    v.string('A URL is required.'),
    v.url(),
    v.check(
      (url: string) => url.startsWith('https://portal.nucleus4d.com/'),
      'The URL must start with "https://portal.nucleus4d.com/".',
    ),
  ),
});

export type DeliverableDataNucleus = v.InferOutput<typeof DeliverableDataNucleusSchema>;

export const DeliverableDataIguideSchema = v.object({
  type: v.literal(DeliverableType.IGUIDE),
  sort: form.optional(form.number()),
  url: v.pipe(
    v.string('A URL is required.'),
    v.url(),
    v.check(
      (url: string) => url.startsWith('https://youriguide.com/'),
      'The URL must start with "https://youriguide.com/".',
    ),
  ),
});

export type DeliverableDataIguide = v.InferOutput<typeof DeliverableDataIguideSchema>;

export const DeliverableDataSphereSchema = v.object({
  type: v.literal(DeliverableType.SPHERE),
  /** The original's pixels, read from its header when it was recognised. */
  width: v.optional(v.number()),
  height: v.optional(v.number()),
});

export type DeliverableDataSphere = v.InferOutput<typeof DeliverableDataSphereSchema>;

export const DeliverableDataSchema = v.variant('type', [
  DeliverableDataPhotoSchema,
  DeliverableDataSocialSchema,
  DeliverableDataVideoSchema,
  DeliverableDataFloorplanSchema,
  DeliverableDataIguideSchema,
  DeliverableDataMatterportSchema,
  DeliverableDataNucleusSchema,
  DeliverableDataSlideshowSchema,
  DeliverableDataAerialSchema,
  DeliverableDataBrochureSchema,
  DeliverableDataSphereSchema,
]);

export type DeliverableData = v.InferOutput<typeof DeliverableDataSchema>;
